import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware } from '@/helpers/forms/axios';

export const senderDomainVerificationStatuses = ['pending', 'verified', 'failed'] as const;
export type SenderDomainVerificationStatusEnum = typeof senderDomainVerificationStatuses[number];

export interface senderDomainDnsRecordInterface {
    type: string
    name: string
    value: string
    valid: boolean
    // Only present on MX records, and only on domains registered after the API
    // started returning it.
    priority?: string
}

export interface senderDomainInterface {
    id: string
    domain: string
    fromEmail: string
    verificationStatus: SenderDomainVerificationStatusEnum
    dnsRecords: senderDomainDnsRecordInterface[]
}

export interface createSenderDomainFormInterface {
    domain: string
    fromEmail: string
}

export type SenderDomainRaw = components['schemas']['SenderDomain'];
export type SenderDomainDnsRecordRaw = SenderDomainRaw['dns_records'][number];

// Aliases the shared component rather than the get-sender-domain operation: the
// spec's SenderDomainOrNull copies SenderDomain's properties without its
// required list, so the operation type marks every field optional although the
// backend serialises the same object for all five operations. Return to the
// operation alias once the spec is fixed.
export interface FetchSenderDomainResponse {
    sender_domain: SenderDomainRaw | null
}

export type CreateSenderDomainResponse = operations['create-sender-domain']['responses']['200']['content']['application/json'];
export type UpdateSenderDomainResponse = operations['update-sender-domain']['responses']['200']['content']['application/json'];
export type VerifySenderDomainResponse = operations['verify-sender-domain']['responses']['200']['content']['application/json'];
export type DeleteSenderDomainResponse = operations['delete-sender-domain']['responses']['200']['content']['application/json'];

export type CreateSenderDomainRequest = operations['create-sender-domain']['requestBody']['content']['application/json'];
export type UpdateSenderDomainRequest = operations['update-sender-domain']['requestBody']['content']['application/json'];

export function toSenderDomainDnsRecord(record: SenderDomainDnsRecordRaw): senderDomainDnsRecordInterface {
    return {
        type: record.type,
        name: record.name,
        value: record.value,
        valid: record.valid,
        // Omit the key rather than set it to undefined, as the case converter did.
        ...(record.priority !== undefined && { priority: record.priority }),
    };
}

export function toSenderDomain(raw: SenderDomainRaw): senderDomainInterface {
    return {
        id: raw.id,
        domain: raw.domain,
        fromEmail: raw.from_email,
        verificationStatus: raw.verification_status,
        dnsRecords: raw.dns_records.map(toSenderDomainDnsRecord),
    };
}

interface SenderDomainsApiReturn {
    fetchSenderDomain: () => Promise<senderDomainInterface | null>
    createSenderDomain: (form: createSenderDomainFormInterface) => Promise<senderDomainInterface>
    updateSenderDomainFromEmail: (fromEmail: string) => Promise<senderDomainInterface>
    verifySenderDomain: () => Promise<senderDomainInterface>
    deleteSenderDomain: () => Promise<void>
}

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): SenderDomainsApiReturn => ({
    fetchSenderDomain: async (): Promise<senderDomainInterface | null> => {
        const response = await axios.get<FetchSenderDomainResponse>('/api/notifications/serviceProviders/senderDomains');

        return response.data.sender_domain === null ? null : toSenderDomain(response.data.sender_domain);
    },

    createSenderDomain: async (form: createSenderDomainFormInterface): Promise<senderDomainInterface> => {
        const body: CreateSenderDomainRequest = {
            domain: form.domain,
            from_email: form.fromEmail,
        };
        const response = await axios.post<CreateSenderDomainResponse>('/api/notifications/serviceProviders/senderDomains', body);

        return toSenderDomain(response.data.sender_domain);
    },

    updateSenderDomainFromEmail: async (fromEmail: string): Promise<senderDomainInterface> => {
        const body: UpdateSenderDomainRequest = { from_email: fromEmail };
        const response = await axios.patch<UpdateSenderDomainResponse>('/api/notifications/serviceProviders/senderDomains', body);

        return toSenderDomain(response.data.sender_domain);
    },

    verifySenderDomain: async (): Promise<senderDomainInterface> => {
        const response = await axios.post<VerifySenderDomainResponse>('/api/notifications/serviceProviders/senderDomains/verify');

        return toSenderDomain(response.data.sender_domain);
    },

    deleteSenderDomain: async (): Promise<void> => {
        await axios.delete<DeleteSenderDomainResponse>('/api/notifications/serviceProviders/senderDomains');
    },
});
